const REPLAY_REGION_ID = "inert-replay";
const LIVE_REGION_SELECTOR = '[role="status"], [aria-live]:not([aria-live="off"])';
const SILENT_ANCESTOR_SELECTOR = '[hidden], [aria-hidden="true"], [inert]';

interface InertWindow {
  snapshot: Map<Element, string>;
  candidates: Set<Element>;
  textAtClose: Map<Element, string>;
}

let current: InertWindow | null = null;
let observer: MutationObserver | null = null;
let pendingSpeech: ReturnType<typeof setTimeout> | null = null;

function replayRegion(): HTMLElement {
  const existing = document.getElementById(REPLAY_REGION_ID);
  if (existing) return existing;
  const region = document.createElement("p");
  region.id = REPLAY_REGION_ID;
  region.className = "sr-only";
  region.setAttribute("aria-live", "polite");
  region.setAttribute("aria-atomic", "true");
  document.body.appendChild(region);
  return region;
}

function liveRegionAbove(node: Node): Element | null {
  const start = node instanceof Element ? node : node.parentElement;
  return start?.closest(LIVE_REGION_SELECTOR) ?? null;
}

function textOf(region: Element): string {
  return (region.textContent ?? "").trim();
}

function snapshotRegions(root: HTMLElement): Map<Element, string> {
  const snapshot = new Map<Element, string>();
  for (const region of root.querySelectorAll(LIVE_REGION_SELECTOR)) {
    snapshot.set(region, textOf(region));
  }
  return snapshot;
}

function noteRegion(region: Element, watched: InertWindow): void {
  watched.candidates.add(region);
  if (textOf(region) === "") watched.snapshot.set(region, "");
}

function noteRecords(records: MutationRecord[], watched: InertWindow): void {
  for (const record of records) {
    const above = liveRegionAbove(record.target);
    if (above) noteRegion(above, watched);
    for (const added of record.addedNodes) {
      if (!(added instanceof Element)) continue;
      if (added.matches(LIVE_REGION_SELECTOR)) noteRegion(added, watched);
      for (const inner of added.querySelectorAll(LIVE_REGION_SELECTOR)) noteRegion(inner, watched);
    }
  }
}

function startObserving(root: HTMLElement, watched: InertWindow): void {
  observer = new MutationObserver((records) => noteRecords(records, watched));
  observer.observe(root, { childList: true, characterData: true, subtree: true });
}

function stopObserving(watched: InertWindow): void {
  if (observer) {
    noteRecords(observer.takeRecords(), watched);
    observer.disconnect();
    observer = null;
  }
  watched.textAtClose = new Map([...watched.candidates].map((region) => [region, textOf(region)]));
}

function sentencesToSpeak(watched: InertWindow): string[] {
  return [...watched.candidates]
    .filter((region) => region.isConnected)
    .filter((region) => region.getAttribute("role") !== "alert")
    .filter((region) => region.closest(SILENT_ANCESTOR_SELECTOR) === null)
    .filter((region) => textOf(region) !== "")
    .filter((region) => textOf(region) !== (watched.snapshot.get(region) ?? ""))
    .filter((region) => textOf(region) === watched.textAtClose.get(region))
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
    .map(textOf);
}

function speak(): void {
  pendingSpeech = null;
  const watched = current;
  current = null;
  if (!watched) return;
  const sentences = sentencesToSpeak(watched);
  if (sentences.length === 0) return;
  replayRegion().textContent = sentences.join(" ");
}

function cancelPendingSpeech(): void {
  if (pendingSpeech === null) return;
  clearTimeout(pendingSpeech);
  pendingSpeech = null;
}

function guarded(work: () => void): () => void {
  return () => {
    try {
      work();
    } catch {
      current = null;
      observer?.disconnect();
      observer = null;
    }
  };
}

function openWindow(root: HTMLElement | null): void {
  cancelPendingSpeech();
  if (observer || !root) return;
  if (!current) {
    replayRegion().textContent = "";
    current = { snapshot: snapshotRegions(root), candidates: new Set(), textAtClose: new Map() };
  }
  startObserving(root, current);
}

function closeWindow(): void {
  if (!current) return;
  stopObserving(current);
  pendingSpeech = setTimeout(guarded(speak), 0);
}

export function syncInertWindow(app: HTMLElement | null, dialogOpen: boolean): void {
  guarded(dialogOpen ? () => openWindow(app) : closeWindow)();
}

export function resetInertWatch(): void {
  cancelPendingSpeech();
  observer?.disconnect();
  observer = null;
  current = null;
  document.getElementById(REPLAY_REGION_ID)?.remove();
}

import.meta.hot?.dispose(resetInertWatch);
